import { createContext, PropsWithChildren, useContext, useEffect, useState, useCallback } from 'react';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import ModuleContext from './ModuleContext';
import { ExerciseCategory, getExerciseCategories } from '../../../api/exercise-category';

type ExerciseCategoriesContextValue = {
  loading: boolean;
  exerciseCategories: ExerciseCategory[];
  fetchExerciseCategories: () => Promise<void>;
};

export const ExerciseCategoriesContext = createContext<ExerciseCategoriesContextValue>({
  loading: true,
  exerciseCategories: [],
  fetchExerciseCategories: () => Promise.resolve(),
});

export function ExerciseCategoriesContextProvider({
  children,
  includeScore,
  includeCompletedExercises,
}: PropsWithChildren<{ includeScore?: boolean; includeCompletedExercises?: boolean }>) {
  const [loading, setLoading] = useState(true);
  const [exerciseCategories, setExerciseCategories] = useState<ExerciseCategory[]>([]);

  const handleError = useThrowAsyncError();

  const { module } = useContext(ModuleContext);

  const fetchExerciseCategories = useCallback(async () => {
    if (!module) return;
    setLoading(true);

    try {
      const { data } = await getExerciseCategories({
        moduleId: module.id,
        includeSubModules: true,
        includeScore,
        includeCompletedExercises,
      });

      const flattenedThemes = [
        ...(data.find(theme => theme.id === 'general')?.categories ?? []),
        ...data.filter(theme => theme.id !== 'general'),
      ];

      setExerciseCategories(flattenedThemes);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [module, includeScore, includeCompletedExercises, handleError]);

  useEffect(() => {
    fetchExerciseCategories();
  }, [fetchExerciseCategories]);

  return (
    <ExerciseCategoriesContext.Provider value={{ exerciseCategories, fetchExerciseCategories, loading }}>
      {children}
    </ExerciseCategoriesContext.Provider>
  );
}

export function useExerciseCategories() {
  return useContext(ExerciseCategoriesContext);
}
